import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../pages/Chatbot.scss'; // Import the CSS file for the chatbot
import '../pages/ChatbotFormatting.css'; // Import additional formatting styles
import options from '../data/doc.json';
import { LdsButton, LdsIcon, LdsTextField, LdsTabs, LdsTabPanel, useLdsModal } from '@elilillyco/ux-lds-react'
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authconfig';
// import Accordion from '../components/Accordion'; // Replaced with custom doc-card UI
import ServiceNowGuidanceModal from '../components/ServiceNowGuidanceModal';
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import envConfig from "../data/env.config.json";
import getCurrentEnv from '../data/utils/getCurrentEnv';

/**
 * Generate a unique session ID for Cortex chat history tracking.
 * Format: sht-<timestamp>-<random> (e.g., sht-1706123456789-a1b2c3d4)
 */
const generateSessionId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10);
  return `sht-${timestamp}-${random}`;
};

// Helper function to format markdown-like text
const formatChatText = (text) => {
  if (!text) return '';

  // Remove embedding UUIDs in various formats
  text = text.replace(/\s*\([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\)\s*/gi, ' ');
  text = text.replace(/\s*\([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\s*,\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})*\)\s*/gi, ' ');
  text = text.replace(/\s+[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s+/gi, ' ');

  // Store code blocks to prevent processing their content
  const codeBlocks = [];
  const inlineCodeBlocks = [];

  // Capture fenced code blocks
  text = text.replace(/```([a-z]*)\n([\s\S]*?)```/g, (match, language, code) => {
    const id = `CODE_BLOCK_${codeBlocks.length}`;
    codeBlocks.push({ code: code.trim(), language: language.trim() });
    return id;
  });

  text = text.replace(/```([\s\S]*?)```/g, (match, code) => {
    const id = `CODE_BLOCK_${codeBlocks.length}`;
    codeBlocks.push({ code: code.trim(), language: '' });
    return id;
  });

  // Capture inline code
  text = text.replace(/`([^`]+)`/g, (match, code) => {
    const id = `INLINE_CODE_${inlineCodeBlocks.length}`;
    inlineCodeBlocks.push(code);
    return id;
  });

  // Process headers
  text = text.replace(/^#### (.*?)$/gm, '<h4>$1</h4>');
  text = text.replace(/^### (.*?)$/gm, '<h3>$1</h3>');
  text = text.replace(/^## (.*?)$/gm, '<h2>$1</h2>');
  text = text.replace(/^# (.*?)$/gm, '<h1>$1</h1>');

  // Process bold and italic
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/(\*|_)(.*?)\1/g, '<em>$2</em>');

  // Process links
  text = text.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Process horizontal rule
  text = text.replace(/^---+$/gm, '<hr>');

  // Process unordered lists (support for multiple list marker types)
  text = text.replace(/^[\s]*[-*+][\s]+(.*?)$/gm, '<li>$1</li>');

  // Process ordered lists
  text = text.replace(/^[\s]*\d+\.[\s]+(.*?)$/gm, '<li>$1</li>');

  // Group list items
  let processedText = '';
  let inList = false;
  let listType = null;

  // Split by line and process each to properly handle lists
  text.split('\n').forEach(line => {
    if (line.includes('<li>')) {
      if (!inList) {
        // Start a new list
        inList = true;
        // Determine list type (ordered or unordered)
        listType = line.match(/^\d+\./) ? 'ol' : 'ul';
        processedText += `<${listType}>`;
      }
      processedText += line;
    } else {
      if (inList) {
        // End the current list
        processedText += `</${listType}>`;
        inList = false;
        listType = null;
      }
      processedText += line + '\n';
    }
  });

  // Close any open list
  if (inList) {
    processedText += `</${listType}>`;
  }

  text = processedText;

  // Process blockquotes
  text = text.replace(/^>[\s]+(.*?)$/gm, '<blockquote>$1</blockquote>');

  // Process tables (simple format)
  // Find table sections (lines with | character)
  const tableRegex = /((?:^[|].*[|]$\n)+)/gm;
  text = text.replace(tableRegex, (tableSection) => {
    // Split into rows
    const rows = tableSection.trim().split('\n');

    // Check if we have a header separator row (e.g., |---|---|)
    const hasHeader = rows.length > 1 && /^\|[-:|]+\|/.test(rows[1]);

    let tableHtml = '<table class="markdown-table">';

    // Process each row
    rows.forEach((row, index) => {
      // Skip the separator row
      if (hasHeader && index === 1) return;

      const isHeader = hasHeader && index === 0;
      const cellType = isHeader ? 'th' : 'td';

      // Extract cells (split by | and remove first and last empty items)
      const cells = row.split('|').slice(1, -1);

      tableHtml += '<tr>';
      cells.forEach(cell => {
        tableHtml += `<${cellType}>${cell.trim()}</${cellType}>`;
      });
      tableHtml += '</tr>';
    });

    tableHtml += '</table>';
    return tableHtml;
  });

  // Restore inline code blocks
  inlineCodeBlocks.forEach((code, index) => {
    text = text.replace(`INLINE_CODE_${index}`, `<code class="inline-code">${code}</code>`);
  });

  // Restore code blocks with language highlighting
  codeBlocks.forEach((codeObj, index) => {
    const { code, language } = codeObj;
    const langClass = language ? ` class="language-${language}"` : '';
    text = text.replace(
      `CODE_BLOCK_${index}`,
      `<pre class="code-block-container"><code${langClass}>${code}</code><button class="code-copy-button" onclick="navigator.clipboard.writeText(this.previousElementSibling.textContent)">Copy</button></pre>`
    );
  });

  // Convert newlines to <br> for proper line breaks (except inside pre tags)
  // First, protect pre tags
  const preTags = [];
  text = text.replace(/<pre[\s\S]*?<\/pre>/g, (match) => {
    const id = `PRE_TAG_${preTags.length}`;
    preTags.push(match);
    return id;
  });

  // Convert newlines
  text = text.replace(/\n/g, '<br>');

  // Restore pre tags
  preTags.forEach((preTag, index) => {
    text = text.replace(`PRE_TAG_${index}`, preTag);
  });

  // Fix duplicate list wrapping
  text = text.replace(/<ul><ul>/g, '<ul>');
  text = text.replace(/<\/ul><\/ul>/g, '</ul>');
  text = text.replace(/<ol><ol>/g, '<ol>');
  text = text.replace(/<\/ol><\/ol>/g, '</ol>');

  return text;
};

// Component to render formatted chat text
const FormattedChatText = ({ text, isStreaming, streamingText }) => {
  const content = isStreaming ? streamingText : text;

  useEffect(() => {
    const addCopyFunctionality = () => {
      const copyButtons = document.querySelectorAll('.code-copy-button');
      copyButtons.forEach(button => {
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);

        newButton.addEventListener('click', () => {
          const codeElement = newButton.previousElementSibling;
          navigator.clipboard.writeText(codeElement.textContent);

          newButton.textContent = 'Copied!';
          newButton.classList.add('copied');

          setTimeout(() => {
            newButton.textContent = 'Copy';
            newButton.classList.remove('copied');
          }, 2000);
        });
      });
    };

    if (content) {
      setTimeout(addCopyFunctionality, 100);
    }
  }, [content, isStreaming]);

  return (
    <div
      className="formatted-chat-text"
      dangerouslySetInnerHTML={{ __html: formatChatText(content) }}
    />
  );
};

function Chatbot(props) {
  useLocation();
  // Get MSAL instance for secure token acquisition
  const { instance, accounts } = useMsal();
  
  // eslint-disable-next-line no-unused-vars
  const { name } = useSelector(({ user }) => user);
  // const userRole = sessionStorage.getItem('role') || '';
  // const isAdminOrSuperAdmin = ['spclty_hub_super_admin', 'spclty_hub_admin'].includes(userRole);
  const [openAccordionId, setOpenAccordionId] = useState(null);
  const handleMainAccordionClick = (id) => {
    setOpenAccordionId(id === openAccordionId ? null : id);
  };
  const [video, setVideo] = useState(false);
  const [data, setData] = useState(options); // Always show all documentation
  const [docSearchQuery, setDocSearchQuery] = useState(''); // Documentation tab search filter

  const [isChatbotOpen, setChatbotOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [currentTab, setCurrentTab] = useState(0); // 0: Ask SpecialtyAI, 1: Documentation
  const [userInput, setUserInput] = useState(""); // State for chat input
  const [messages, setMessages] = useState([
    { text: "Hello! How can I assist you with Specialty Hub today?", isBot: true }
  ]);
  const messagesEndRef = useRef(null); // Reference for auto-scrolling
  const [isLoading, setIsLoading] = useState(false);
  const isSendingRef = useRef(false); // Synchronous guard against double-sends
  const [streamingText, setStreamingText] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [isStreaming, setIsStreaming] = useState(false);
  
  // Session ID for Cortex multi-turn chat history
  const [sessionId, setSessionId] = useState(() => generateSessionId());

  /**
   * Sanitize user input to prevent XSS attacks and malicious content.
   * Returns { isValid: boolean, sanitizedText: string, errorMessage?: string }
   */
  const sanitizeInput = (input) => {
    if (!input || typeof input !== 'string') {
      return { isValid: false, sanitizedText: '', errorMessage: 'Invalid input' };
    }

    const trimmedInput = input.trim();

    // Check for potentially malicious patterns
    const dangerousPatterns = [
      /<script[^>]*>.*?<\/script>/gi,  // Script tags
      /<script[^>]*>/gi,                // Opening script tags
      /<\/script>/gi,                   // Closing script tags
      /javascript:/gi,                  // JavaScript protocol
      /on\w+\s*=/gi,                    // Event handlers (onclick, onerror, etc.)
      /<iframe[^>]*>/gi,                // Iframe tags
      /<object[^>]*>/gi,                // Object tags
      /<embed[^>]*>/gi,                 // Embed tags
      /<link[^>]*>/gi,                  // Link tags
      /<meta[^>]*>/gi,                  // Meta tags
      /<style[^>]*>/gi,                 // Style tags
      /data:/gi,                        // Data URIs
      /vbscript:/gi,                    // VBScript protocol
      /expression\s*\(/gi,             // CSS expressions
    ];

    for (const pattern of dangerousPatterns) {
      if (pattern.test(trimmedInput)) {
        return {
          isValid: false,
          sanitizedText: '',
          errorMessage: 'Your message contains content that is not allowed. Please rephrase your question.'
        };
      }
    }

    // Remove HTML tags but keep the text content
    const sanitizedText = trimmedInput
      .replace(/<[^>]*>/g, '')  // Remove all HTML tags
      .replace(/&lt;/g, '<')    // Decode common entities for display
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'");

    // Check if the sanitized text is too short or only whitespace
    if (sanitizedText.trim().length === 0) {
      return {
        isValid: false,
        sanitizedText: '',
        errorMessage: 'Please enter a valid question.'
      };
    }

    // Check max length (prevent extremely long inputs)
    if (sanitizedText.length > 2000) {
      return {
        isValid: false,
        sanitizedText: '',
        errorMessage: 'Your message is too long. Please limit your question to 2000 characters.'
      };
    }

    return { isValid: true, sanitizedText };
  };

  // ServiceNow guidance modal state
  const { isModalOpen: isServiceNowModalOpen, setIsModalOpen: setIsServiceNowModalOpen } = useLdsModal();

  // Function to scroll to the bottom of the messages
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  // Auto-scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Auto-focus input field when switching to chat tab
  useEffect(() => {
    if (currentTab === 0) {
      const inputElement = document.getElementById('chatInput');
      if (inputElement) {
        setTimeout(() => inputElement.focus(), 100);
      }
    }
  }, [currentTab]);

  const toggleChatbot = () => {
    const newState = !isChatbotOpen;
    setChatbotOpen(newState);
    if (!newState) setIsMaximized(false);

    if (newState) {
      setTimeout(() => {
        scrollToBottom();
        window.dispatchEvent(new Event('resize'));
      }, 100);
    }
  };

  const toggleMaximize = () => {
    setIsMaximized(prev => !prev);
    setTimeout(() => scrollToBottom(), 100);
  };

  const openServiceNowGuidance = () => {
    setIsServiceNowModalOpen(true);
  };

  // Start a new chat session — clears messages and generates a new session ID
  const handleNewChat = useCallback(() => {
    setMessages([
      { text: "Hello! How can I assist you with Specialty Hub today?", isBot: true }
    ]);
    setSessionId(generateSessionId());
    setUserInput('');
    setStreamingText('');
    setIsStreaming(false);
    setIsLoading(false);
    isSendingRef.current = false;
  }, []);

  const playVideo = (link) => {
    window.open(link);
    setVideo(true);
  };

  const viewDoc = (link) => {
    window.open(link);
    setVideo(true);
  };

  // Function to simulate streaming effect
  const simulateStreamingEffect = (text, index = 0) => {
    if (index <= text.length) {
      setStreamingText(text.substring(0, index));
      setTimeout(() => {
        simulateStreamingEffect(text, index + 3);
      }, 5);
    } else {
      setIsStreaming(false);
      isSendingRef.current = false;
    }
  };

  // Handle sending a message in the chat widget
  const handleSendMessage = async () => {
    if (userInput.trim() === "" || isLoading || isStreaming || isSendingRef.current) return;

    // Sanitize input to prevent XSS and malicious content
    const { isValid, sanitizedText, errorMessage } = sanitizeInput(userInput);
    
    if (!isValid) {
      // Show error message to user
      setMessages(prevMessages => [
        ...prevMessages,
        { text: userInput, isBot: false },
        { text: errorMessage || 'Invalid input. Please try again.', isBot: true, isError: true }
      ]);
      setUserInput("");
      return;
    }

    isSendingRef.current = true;

    // Add user message to chat
    const newMessages = [
      ...messages,
      { text: sanitizedText, isBot: false }
    ];

    setMessages(newMessages);
    const questionText = sanitizedText;
    setUserInput("");
    setIsLoading(true);

    try {
      // Get token from MSAL cache securely (no sessionStorage)
      let token = null;
      if (accounts && accounts.length > 0) {
        try {
          const resp = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
          token = resp.accessToken;
        } catch (error) {
          console.error('[Chatbot] Error acquiring token from MSAL:', error);
          throw new Error('Failed to acquire authentication token. Please try again.');
        }
      }
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Get API URL from environment config
      const env = getCurrentEnv();
      const apiUrl = envConfig[env].api_url;
      const modelSuffix = { dev: '-dev', qa: '-qa', prd: '', prod: '' };
      const modelName = `specialty-hub-tool${modelSuffix[env] ?? '-dev'}`;

      const encodedQuestion = encodeURIComponent(questionText);

      const response = await fetch(`${apiUrl}/cortex-api-data?question=${encodedQuestion}&model=${modelName}&session_id=${sessionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const responseData = await response.json();
      const answerText = (responseData?.data?.answer || '').trim();
      const MIN_SOURCE_SCORE = 0.5;
      const answerSources = ((responseData.data && responseData.data.sources) || []).filter(
        src => typeof src.score === 'number' && src.score >= MIN_SOURCE_SCORE
      );

      // Start streaming effect
      setIsLoading(false);
      setIsStreaming(true);

      // Add empty message that will be populated by streaming effect
      setMessages([
        ...newMessages,
        {
          text: '',
          isBot: true,
          isStreaming: true,
          sources: answerSources
        }
      ]);

      // Start streaming effect
      simulateStreamingEffect(answerText);

      // After streaming completes, update the message with full text
      setTimeout(() => {
        setMessages(prevMessages => {
          const updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1].text = answerText;
          updatedMessages[updatedMessages.length - 1].isStreaming = false;
          updatedMessages[updatedMessages.length - 1].sources = answerSources;
          return updatedMessages;
        });

        // Enable clicking links
        setTimeout(() => {
          const links = document.querySelectorAll('.formatted-chat-text a');
          links.forEach(link => {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
          });
        }, 100);
      }, Math.ceil(answerText.length / 3) * 5 + 100);
    } catch (error) {
      console.error("Error sending message:", error);
      setIsLoading(false);
      isSendingRef.current = false;

      let errorMessage = "Sorry, I encountered an error processing your request. Please try again later.";

      if (error.message.includes('Authentication token not found')) {
        errorMessage = "Your session has expired. Please refresh the page and log in again.";
      } else if (error.message.includes('status 401') || error.message.includes('status 403')) {
        errorMessage = "You don't have permission to access this resource. Please check your credentials.";
      }

      setMessages([
        ...newMessages,
        {
          text: errorMessage,
          isBot: true
        }
      ]);
    }
  };

  // Handle Enter key press in chat input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading && !isStreaming && userInput.trim() !== "") {
      handleSendMessage();
    }
  };

  return (
    <>
      {video && <div className='player'>
      </div>}
      {/* Chatbot Initiate Button */}
      {!isChatbotOpen ? (
        <div className="chatbot-init-button">
          <div className="chatbot-init-open" onClick={toggleChatbot}>
            <LdsIcon name="ChatsFill" label={"Specialty Hub"} inline />
          </div>
        </div>
      ) : (
        <div className='chatbot-init-button'>
          <div className='chatbot-init-close' onClick={toggleChatbot}>
            <LdsIcon name="X" inline />
          </div>
        </div>
      )}

      {/* Chatbot Layout */}
      {isChatbotOpen && (
        <>
          <div className={`chatbot-layout ${isMaximized ? 'chatbot-maximized' : ''}`}>
            {/* Main chatbot content area */}
            <div className="chatbot-main-content">
              <div className="chatbot-header">
  
              <div className="chatbot-widget-selector">
                  <LdsTabs
                    activeTab={currentTab}
                    onChange={(newTabId) => {
                      setCurrentTab(newTabId);
                      setTimeout(() => {
                        const chatbotLayout = document.querySelector('.chatbot-layout');
                        if (chatbotLayout) {
                          chatbotLayout.scrollTop = 0;
                        }
                      }, 50);
                    }}
                    tabLabels={[
                      { tabId: 0, label: 'Ask SpecialtyAI ✨' },
                      { tabId: 1, label: 'Documentation' },
                    ]}
                  >
                    <LdsTabPanel tabId={0} activeTab={currentTab}>
                      {/* New Chat and Maximize buttons */}
                      <div className="chatbot-session-controls">
                        <button
                          className="new-chat-button"
                          onClick={handleNewChat}
                          disabled={isLoading || isStreaming || messages.length <= 1}
                          title="Start a new conversation"
                        >
                          <LdsIcon name="ArrowCounterClockwiseFill" inline />
                          <span>New Chat</span>
                        </button>
                        <button
                          className="chatbot-maximize-button"
                          onClick={toggleMaximize}
                          title={isMaximized ? 'Minimize chat' : 'Maximize chat'}
                        >
                          <LdsIcon name={isMaximized ? 'ArrowsInSimple' : 'ArrowsOutSimple'} inline />
                        </button>
                      </div>
                      {/* Chat Widget */}
                      <div className="chatbot-conversation">
                        <div className="chatbot-messages-container">
                          {messages.map((message, index) => (
                            <div key={index} className={`chat-message ${message.isBot ? 'bot-message' : 'user-message'}${message.isError ? ' error-message' : ''}`}>
                              {message.isBot && (
                                <div className="bot-avatar">
                                  <img src={require('../assets/images/specialtyhub_logo.png')} alt="Specialty Hub Assistant" className="specialtyhub-logo" />
                                </div>
                              )}
                              <div className="message-text">
                                {message.isBot ? (
                                  <FormattedChatText
                                    text={message.text}
                                    isStreaming={message.isStreaming}
                                    streamingText={streamingText}
                                  />
                                ) : (
                                  <div className="user-message-text">
                                    {message.text}
                                  </div>
                                )}
                                {message.isBot && !message.isStreaming && message.sources && message.sources.length > 0 && (
                                  <div className="chatbot-sources">
                                    <div className="chatbot-sources-heading">Sources</div>
                                    <ul className="chatbot-sources-list">
                                      {message.sources.map((src, sIdx) => (
                                        <li key={`${index}-src-${sIdx}`} className="chatbot-source-item" title={src.snippet || ''}>
                                          <span className="chatbot-source-name">{src.fileName}</span>
                                          {src.page != null && (
                                            <span className="chatbot-source-page"> · p.{src.page}</span>
                                          )}
                                          {typeof src.score === 'number' && src.score >= 0 && src.score <= 1 && (
                                            <span className="chatbot-source-score"> · {(src.score * 100).toFixed(0)}%</span>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          {isLoading && (
                            <div className="chat-message bot-message">
                              <div className="bot-avatar">
                                <img src={require('../assets/images/specialtyhub_logo.png')} alt="Specialty Hub Assistant" className="specialtyhub-logo" />
                              </div>
                              <div className="message-text">
                                <div className="loading-indicator">
                                  <span className="dot"></span>
                                  <span className="dot"></span>
                                  <span className="dot"></span>
                                </div>
                                <div className="loading-text">Processing your request...</div>
                              </div>
                            </div>
                          )}
                          <div
                            ref={messagesEndRef}
                            style={{
                              float: "left",
                              clear: "both",
                              width: "100%",
                              height: "1px"
                            }}
                          />
                        </div>
                      </div>
                    </LdsTabPanel>

                    <LdsTabPanel tabId={1} activeTab={currentTab}>
                      {/* Documentation Widget */}
                      <div className="chatbot-messages doc-panel">
                        {/* Search/filter input */}
                        <div className="doc-search-container">
                          <LdsIcon name="MagnifyingGlass" inline />
                          <input
                            type="text"
                            className="doc-search-input"
                            placeholder="Search documentation..."
                            value={docSearchQuery}
                            onChange={(e) => setDocSearchQuery(e.target.value)}
                            autoComplete="off"
                          />
                          {docSearchQuery && (
                            <button className="doc-search-clear" onClick={() => setDocSearchQuery('')}>
                              <LdsIcon name="X" inline />
                            </button>
                          )}
                        </div>
                        {/* Documentation cards */}
                        <div className="doc-cards-list">
                          {Object.keys(data)
                            .filter((key) => {
                              if (!docSearchQuery.trim()) return true;
                              const q = docSearchQuery.toLowerCase();
                              return key.toLowerCase().includes(q) ||
                                (data[key]?.description || '').toLowerCase().includes(q);
                            })
                            .map((key) => {
                              let subOptions = data[key]?.features || {};
                              return (
                                <div key={key} className="doc-card">
                                  <div
                                    className={`doc-card-header ${key === openAccordionId ? 'doc-card-header--open' : ''}`}
                                    onClick={() => handleMainAccordionClick(key)}
                                  >
                                    <div className="doc-card-header-left">
                                      <span className="doc-card-icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                                          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                                        </svg>
                                      </span>
                                      <span className="doc-card-title">{key}</span>
                                    </div>
                                    <span className={`doc-card-chevron ${key === openAccordionId ? 'doc-card-chevron--open' : ''}`}>
                                      <LdsIcon name="CaretDown" inline />
                                    </span>
                                  </div>
                                  {key === openAccordionId && (
                                    <div className="doc-card-body">
                                      <p className="doc-card-description">{data[key]?.description}</p>
                                      <div className="doc-card-actions">
                                        {data[key]?.doc && (
                                          <button className="doc-action-btn doc-action-btn--outline" onClick={() => viewDoc(data[key]?.doc)}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                              <polyline points="14 2 14 8 20 8"/>
                                              <line x1="16" y1="13" x2="8" y2="13"/>
                                              <line x1="16" y1="17" x2="8" y2="17"/>
                                              <polyline points="10 9 9 9 8 9"/>
                                            </svg>
                                            <span>Documentation</span>
                                          </button>
                                        )}
                                        {data[key]?.link && (
                                          <button className="doc-action-btn doc-action-btn--filled" onClick={() => playVideo(data[key]?.link)}>
                                            <LdsIcon name="PlayFill" inline />
                                            <span>Video Guide</span>
                                          </button>
                                        )}
                                      </div>
                                      {Object.keys(subOptions).length > 0 && (
                                        <div className="doc-sub-features">
                                          {Object.keys(subOptions).map((subKey) => (
                                            <div key={subKey} className="doc-sub-item">
                                              <div
                                                className="doc-sub-item-header"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  subOptions[subKey].isOpen = !subOptions[subKey]?.isOpen;
                                                  setData({ ...data, [key]: { ...data[key], features: { ...subOptions } } });
                                                }}
                                              >
                                                <span className="doc-sub-item-title">{subKey}</span>
                                                <span className={`doc-sub-chevron ${subOptions[subKey]?.isOpen ? 'doc-sub-chevron--open' : ''}`}>
                                                  <LdsIcon name="CaretDown" inline />
                                                </span>
                                              </div>
                                              {subOptions[subKey]?.isOpen && (
                                                <div className="doc-sub-item-body">
                                                  <p>{subOptions[subKey]?.description}</p>
                                                  {subOptions[subKey]?.link && (
                                                    <button className="doc-action-btn doc-action-btn--filled doc-action-btn--sm" onClick={() => playVideo(subOptions[subKey].link)}>
                                                      <LdsIcon name="PlayFill" inline />
                                                      <span>Play Demo</span>
                                                    </button>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          {Object.keys(data).filter((key) => {
                            if (!docSearchQuery.trim()) return true;
                            const q = docSearchQuery.toLowerCase();
                            return key.toLowerCase().includes(q) || (data[key]?.description || '').toLowerCase().includes(q);
                          }).length === 0 && (
                            <div className="doc-empty-state">
                              <LdsIcon name="MagnifyingGlass" inline />
                              <p>No documentation found for "<strong>{docSearchQuery}</strong>"</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </LdsTabPanel>
                  </LdsTabs>
                </div>
              </div>
            </div>
          {/* Sticky Input Container - pinned to bottom */}
          {currentTab === 0 && (
            <div className="chatbot-input-container sticky-input">
              {/* File attachment UI removed */}
              <div className="chatbot-input-row">
                <LdsTextField
                  className="chat-input"
                  id="chatInput"
                  placeholder="Ask me anything about Specialty Hub..."
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  fullWidth
                  autoComplete="off"
                />
                <LdsButton
                  className="send-button"
                  onClick={handleSendMessage}
                  disabled={!userInput.trim() || isLoading || isStreaming}
                >
                  Send
                  <LdsIcon
                    name="CaretUpFill"
                    label="Send"
                    inline
                  />
                </LdsButton>
              </div>
            </div>
          )}
          {/* Service Now Incident button - integrated at the bottom of the layout */}
          <div className="chatbot-buttons">
            <LdsButton
              onClick={openServiceNowGuidance}
              icon="WarningFill"
              iconPosition="after"
            >
              Service Now Incident
            </LdsButton>
          </div>
          {/* End chatbot-main-content */}
          </div>
        </>
      )}
      
      {/* ServiceNow Guidance Modal */}
      <ServiceNowGuidanceModal 
        isModalOpen={isServiceNowModalOpen}
        setIsModalOpen={setIsServiceNowModalOpen}
      />
    </>
  );
}

export default Chatbot;
